import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Upload, Globe, Layers, Play, BookTemplate,
  Settings, LogOut, ChevronLeft, ChevronRight, Menu, Building2,
  Users, Mail, ClipboardList, ShieldCheck, Zap, ChevronDown, ChevronUp,
  Calendar, Webhook, BarChart2, Table2, Key, Code2, FileJson, GitMerge, GitBranch, Package, CreditCard
} from "lucide-react";

const POWER_ITEMS = [
  { path: "/power", label: "Hub Overview", icon: Zap },
  { path: "/power/sequences", label: "Execution Sequences", icon: GitBranch },
  { path: "/power/schedules", label: "Scheduled Runs", icon: Calendar },
  { path: "/power/webhooks", label: "Webhook Triggers", icon: Webhook },
  { path: "/power/analytics", label: "Analytics", icon: BarChart2 },
  { path: "/power/profiler", label: "Data Profiler", icon: Table2 },
  { path: "/power/variables", label: "Variables Store", icon: Key },
  { path: "/power/functions", label: "Custom Functions", icon: Code2 },
  { path: "/power/validation", label: "Validation Rules", icon: ShieldCheck },
  { path: "/power/openapi", label: "Import OpenAPI", icon: FileJson },
  { path: "/power/merge", label: "Multi-File Merge", icon: GitMerge },
  { path: "/power/export-import", label: "Export / Import", icon: Package },
];

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [powerOpen, setPowerOpen] = useState(
    () => window.location.pathname === "/power" || window.location.pathname.startsWith("/power/")
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isAdmin = ["super_admin", "company_admin"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";
  const isReadOnly = user?.role === "company_user";

  const nav = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard, show: true },
    { path: "/upload", label: "Upload File", icon: Upload, show: true },
    { path: "/lookups", label: "Lookup Tables", icon: Layers, show: !isReadOnly },
    { path: "/definitions", label: "API Definitions", icon: Globe, show: !isReadOnly },
    { path: "/templates", label: "Templates", icon: BookTemplate, show: true },
    { path: "/global-resources", label: "Global Resources", icon: ShieldCheck, show: true },
    { path: "/executions", label: "Executions", icon: Play, show: true },
    { path: "/admin", label: "Admin Settings", icon: Settings, show: isAdmin, divider: true },
    { path: "/admin/users", label: "User Management", icon: Users, show: isAdmin },
    { path: "/admin/mailboxes", label: "IMAP Mailboxes", icon: Mail, show: isAdmin },
    { path: "/admin/audit", label: "Audit Log", icon: ClipboardList, show: isAdmin },
    { path: "/admin/companies", label: "Companies", icon: Building2, show: isSuperAdmin },
    { path: "/admin/pricing", label: "Pricing Plans", icon: CreditCard, show: isSuperAdmin },
  ].filter(n => n.show);

  const isActive = (path) => {
    if (path === "/" || path === "/power") return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const isPowerActive = location.pathname === "/power" || location.pathname.startsWith("/power/");

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const roleLabel = { super_admin: "Super Admin", company_admin: "Admin", company_user: "User" };
  const roleBg = { super_admin: "bg-purple-100 text-purple-700", company_admin: "bg-[#e6f2f5] text-[#006080]", company_user: "bg-gray-100 text-gray-500" };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-gray-200 ${collapsed ? "justify-center" : ""}`}>
        <div className="w-8 h-8 bg-[#006080] rounded-md flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">X</span>
        </div>
        {!collapsed && (
          <div>
            <p className="font-bold text-[#006080] text-sm leading-tight">ExcelToAPI</p>
            <p className="text-xs text-gray-400">Transform Platform</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
        {nav.map(({ path, label, icon: Icon, divider }) => (
          <React.Fragment key={path}>
            {divider && !collapsed && <div className="border-t border-gray-100 my-2" />}
            <Link
              to={path}
              className={`nav-item ${isActive(path) ? "active" : ""} ${collapsed ? "justify-center px-2" : ""}`}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? label : undefined}
            >
              <Icon size={16} className="flex-shrink-0" />
              {!collapsed && <span className="text-sm">{label}</span>}
            </Link>
          </React.Fragment>
        ))}

        {/* Power Suite group */}
        {!collapsed && <div className="border-t border-gray-100 my-2" />}
        {collapsed ? (
          <Link to="/power" className={`nav-item justify-center px-2 ${isPowerActive ? "active" : ""}`} title="Power Suite">
            <Zap size={16} className="flex-shrink-0" />
          </Link>
        ) : (
          <div>
            <button
              className={`nav-item w-full justify-between ${isPowerActive ? "bg-[#006080] text-white" : "hover:bg-[#e6f2f5] hover:text-[#006080]"}`}
              onClick={() => setPowerOpen(o => !o)}
            >
              <div className="flex items-center gap-2">
                <Zap size={16} className="flex-shrink-0" />
                <span className="text-sm font-semibold">Power Suite</span>
              </div>
              {powerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {powerOpen && (
              <div className="ml-3 mt-0.5 border-l-2 border-[#006080]/20 pl-2 space-y-0.5">
                {POWER_ITEMS.map(({ path, label, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    className={`nav-item text-xs py-1.5 ${isActive(path) ? "active" : ""}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    <Icon size={13} className="flex-shrink-0" />
                    <span>{label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* User + logout */}
      <div className={`px-2 py-3 border-t border-gray-200 ${collapsed ? "px-1" : ""}`}>
        {!collapsed && (
          <div className="px-3 py-2 mb-1">
            <p className="text-xs font-semibold text-gray-800 truncate">{user?.name}</p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
        )}
        <button onClick={handleLogout} className={`nav-item w-full text-red-500 hover:bg-red-50 hover:text-red-600 ${collapsed ? "justify-center px-2" : ""}`} data-testid="logout-button">
          <LogOut size={16} className="flex-shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8F9FA]">
      <aside className={`hidden md:flex flex-col flex-shrink-0 bg-white border-r border-gray-200 transition-all duration-200 ${collapsed ? "w-[56px]" : "w-[220px]"}`}>
        <button onClick={() => setCollapsed(!collapsed)} className="absolute z-20 w-5 h-5 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-400 hover:text-[#006080]" style={{ left: collapsed ? 44 : 208, top: "50%" }}>
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-[220px] h-full bg-white shadow-xl"><SidebarContent /></aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-14 bg-white border-b border-gray-200 flex items-center px-4 gap-3 flex-shrink-0">
          <button className="md:hidden p-1.5 rounded-md hover:bg-gray-100" onClick={() => setMobileOpen(true)}>
            <Menu size={18} />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${roleBg[user?.role] || "bg-gray-100"}`}>
              {roleLabel[user?.role] || user?.role}
            </span>
            <button onClick={() => navigate("/profile")} className="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" title="My Profile">
              <div className="w-7 h-7 rounded-full bg-[#006080] flex items-center justify-center text-white text-xs font-bold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="hidden sm:block text-sm font-medium text-gray-700">{user?.name}</span>
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
